import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { MatRadioModule } from '@angular/material/radio';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DateAdapter, MatOptionModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import { NGXLogger } from 'ngx-logger';
import moment from 'moment';

import { GT2PageAbstract } from '../../../content/abstract/GT2PageAbstract';
import { FuseTranslationLoaderService } from '../../../../../@fuse/services/translation-loader.service';
import { ClientsService } from '../../../api/clients.service';
import { ConseillersService } from '../../../api/conseillers.service';
import { SubmissionsService } from '../../../api/submissions.service';
import {
  ConvertSubmissionPayload,
  SubmissionDocumentType,
  SubmissionModel,
} from '../../../api/models/SubmissionModel';
import { ConvertSubmissionPanelService } from '../../../services/convert-submission-panel.service';
import { Settings } from '../../../settings/settings';
import { GT2CalendarUtils } from '../../../utils/GT2CalendarUtils';
import { GT2DateAdapter } from '../../../utils/GT2DateAdapter';
import { Gt2SelectComponent } from '../../gt2-select/gt2-select.component';
import { locale as english } from './i18n/en-CA';
import { locale as french } from './i18n/fr-CA';

type ClientMode = 'match' | 'create' | 'choose';

/**
 * Right panel « Convertir la soumission » — turns a pending web submission into
 * a Proposal or an Order in ONE API call (POST /v1/submissions/{uuid}/convert):
 * client (matched by email / created / chosen), conseiller, name, date, time,
 * guests. Resolves the panel with the created Event.
 */
@Component({
  selector: 'app-convert-submission',
  templateUrl: './convert-submission.component.html',
  styleUrls: ['./convert-submission.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatRadioModule,
    MatInputModule,
    MatFormFieldModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatAutocompleteModule,
    MatDatepickerModule,
    MatOptionModule,
    NgxMaterialTimepickerModule,
    TranslateModule,
    Gt2SelectComponent,
  ],
  providers: [{ provide: DateAdapter, useClass: GT2DateAdapter }],
})
export class ConvertSubmissionComponent
  extends GT2PageAbstract
  implements OnInit, OnDestroy
{
  submission!: SubmissionModel;
  form: UntypedFormGroup;
  documentType: SubmissionDocumentType = 'PROPOSAL';
  event: any = {
    name: null,
    event_date: moment().format('YYYY-MM-DD'),
    event_date_end: null,
    event_time: null,
    event_time_end: null,
    guest_count: null,
  };
  override loading: boolean = false;
  startTimeSelected: boolean = false;
  eventDateTimeRangeInvalid: boolean = false;

  // Conseiller (responsible employee)
  responsibleEmployee: any = null;

  // Client
  clientMode: ClientMode = 'create';
  clientSearchCtrl: UntypedFormControl = new UntypedFormControl('');
  chosenClient: any = null;
  clientResults: any[] = [];
  clientSearching: boolean = false;

  private clientSearch$ = new Subject<string>();
  private subs: Subscription[] = [];

  constructor(
    private panelService: ConvertSubmissionPanelService,
    private translationLoader: FuseTranslationLoaderService,
    public translate: TranslateService,
    private formBuilder: UntypedFormBuilder,
    private submissionsService: SubmissionsService,
    private clientsService: ClientsService,
    public conseillersService: ConseillersService,
    private adapter: DateAdapter<any>,
    public dialog: MatDialog,
    public router: Router,
    private logger: NGXLogger,
  ) {
    super();
    this.translationLoader.loadTranslations(english, french);
    this.adapter.setLocale(this.translate.currentLang);

    const data = this.panelService.getData();
    if (!data) {
      throw new Error('ConvertSubmissionComponent opened without a submission');
    }
    this.submission = data.submission;
    this.prefillFromSubmission(this.submission);

    this.form = this.formBuilder.group({
      name: [
        '',
        [
          Validators.minLength(Settings.inputMinChar),
          Validators.maxLength(Settings.inputMaxCharMid),
          Validators.required,
        ],
      ],
      event_date: ['', [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.subs.push(
      this.clientSearch$
        .pipe(debounceTime(400), distinctUntilChanged())
        .subscribe((value) => this.doClientSearch(value)),
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
    this.subs = [];
  }

  // -----------------------------------------------------------------------------
  // Prefill
  // -----------------------------------------------------------------------------
  private prefillFromSubmission(submission: SubmissionModel): void {
    this.event.name =
      submission.event_place_and_name?.trim() || submission.full_name;
    if (submission.event_date) {
      this.event.event_date = moment(submission.event_date).format(
        'YYYY-MM-DD',
      );
    }
    const time = ConvertSubmissionComponent.parseTime(submission.event_time);
    if (time) {
      this.event.event_time = time;
      this.startTimeSelected = true;
    }
    this.event.guest_count = submission.guest_count_hint ?? null;
    this.clientMode = submission.client_match ? 'match' : 'create';
  }

  /** « 17:30 », « 17h30 », « vers 17h », « 5:00 PM » → « HH:mm », else null. */
  static parseTime(raw: string | null | undefined): string | null {
    if (!raw) {
      return null;
    }
    const pm = /p\.?\s*m\.?/i.test(raw);
    const match = raw.match(/(\d{1,2})\s*[:hH]\s*(\d{2})?/);
    if (!match) {
      return null;
    }
    let hours = parseInt(match[1], 10);
    const minutes = match[2] ? parseInt(match[2], 10) : 0;
    if (pm && hours < 12) {
      hours += 12;
    }
    if (hours > 23 || minutes > 59) {
      return null;
    }
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  // -----------------------------------------------------------------------------
  // Date / time (mirrors QuickOrderComponent)
  // -----------------------------------------------------------------------------
  public onDatePickerChange(event: any): void {
    if (!event.value) {
      return;
    }
    this.event.event_date = GT2CalendarUtils.formatDateForAPI(
      new Date(event.value),
    );
    this.refreshEventDateTimeRangeInvalid();
  }

  public onDatePickerChangeEnd(event: any): void {
    this.event.event_date_end = event.value
      ? GT2CalendarUtils.formatDateForAPI(new Date(event.value))
      : null;
    this.refreshEventDateTimeRangeInvalid();
  }

  public onStartTimeChange(time: string): void {
    this.event.event_time = time;
    this.startTimeSelected = !!(time && time !== '' && time !== 'Invalid DateTime');
    this.refreshEventDateTimeRangeInvalid();
  }

  public onEndTimeChange(_time?: string): void {
    this.refreshEventDateTimeRangeInvalid();
  }

  public addThreeHoursToEndTime(): void {
    if (this.event.event_time && this.event.event_time !== 'Invalid DateTime') {
      this.event.event_time_end = moment(this.event.event_time, 'HH:mm')
        .add(3, 'hours')
        .format('HH:mm');
      this.refreshEventDateTimeRangeInvalid();
    }
  }

  public refreshEventDateTimeRangeInvalid(): void {
    const t0 = this.event?.event_time;
    const t1 = this.event?.event_time_end;
    if (!t0 || t0 === 'Invalid DateTime' || !t1 || t1 === 'Invalid DateTime') {
      this.eventDateTimeRangeInvalid = false;
      return;
    }
    const dateStart = this.event?.event_date;
    const dateEnd = this.event?.event_date_end || dateStart;
    const start = moment(`${dateStart} ${t0}`, 'YYYY-MM-DD HH:mm', true);
    const end = moment(`${dateEnd} ${t1}`, 'YYYY-MM-DD HH:mm', true);
    this.eventDateTimeRangeInvalid =
      start.isValid() && end.isValid() ? end.isBefore(start) : false;
  }

  // -----------------------------------------------------------------------------
  // Conseiller
  // -----------------------------------------------------------------------------
  public onResponsibleEmployeeSelected(employee: any): void {
    this.responsibleEmployee = employee ?? null;
  }

  // -----------------------------------------------------------------------------
  // Client
  // -----------------------------------------------------------------------------
  public onClientInput(value: any): void {
    if (typeof value === 'string') {
      this.clientSearch$.next(value);
    }
  }

  private doClientSearch(keyword: string): void {
    if (!keyword || keyword.length < 2) {
      this.clientResults = [];
      return;
    }
    this.clientSearching = true;
    this.clientsService.getAllClientsCC(keyword).subscribe({
      next: (response: any) => {
        this.clientResults = response?.data || [];
        this.clientSearching = false;
      },
      error: (error: any) => {
        this.logger.error(
          'ConvertSubmissionComponent.doClientSearch() -> ERROR: ' +
            JSON.stringify(error),
        );
        this.clientSearching = false;
        this.handleAPIError(error, this.dialog, null, null, this.router);
      },
    });
  }

  public displayClient = (client: any): string => {
    if (!client) {
      return '';
    }
    if (typeof client === 'string') {
      return client;
    }
    return client.name_label ?? client.full_name ?? '';
  };

  public onClientSelected(client: any): void {
    this.chosenClient = client;
  }

  public clearChosenClient(): void {
    this.chosenClient = null;
    this.clientResults = [];
    this.clientSearchCtrl.setValue('', { emitEvent: false });
  }

  // -----------------------------------------------------------------------------
  // Save / cancel
  // -----------------------------------------------------------------------------
  public get clientReady(): boolean {
    switch (this.clientMode) {
      case 'match':
        return !!this.submission.client_match;
      case 'choose':
        return !!this.chosenClient?.uuid;
      default:
        return true;
    }
  }

  public get canSave(): boolean {
    return (
      this.form.valid &&
      this.clientReady &&
      !!this.event.event_time &&
      this.event.event_time !== 'Invalid DateTime' &&
      this.event.event_time_end !== 'Invalid DateTime' &&
      !this.eventDateTimeRangeInvalid &&
      !this.loading
    );
  }

  public onSave(): void {
    if (!this.canSave) {
      return;
    }
    this.loading = true;

    const payload: ConvertSubmissionPayload = {
      type: this.documentType,
      name: this.event.name,
      event_date: this.event.event_date,
      event_date_end: this.event.event_date_end,
      event_time: this.event.event_time,
      event_time_end: this.event.event_time_end || null,
      guest_count:
        this.event.guest_count === null || this.event.guest_count === ''
          ? null
          : Number(this.event.guest_count),
      responsible_employee_uuid: this.responsibleEmployee?.uuid ?? null,
    };
    if (this.clientMode === 'match' && this.submission.client_match) {
      payload.client_uuid = this.submission.client_match.uuid;
    } else if (this.clientMode === 'choose') {
      payload.client_uuid = this.chosenClient.uuid;
    } else {
      payload.create_client = true;
    }

    this.submissionsService
      .convertSubmission(this.submission.uuid, payload)
      .subscribe({
        next: (response: any) => {
          this.loading = false;
          this.panelService.emitResult(response.data);
        },
        error: (error: any) => {
          this.logger.error(
            'ConvertSubmissionComponent.onSave() -> ERROR: ' +
              JSON.stringify(error),
          );
          this.loading = false;
          this.handleAPIError(error, this.dialog, null, null, this.router);
        },
      });
  }

  public close(): void {
    this.panelService.emitResult(false);
  }
}
